import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import useQueryString, { useSilentPersistQueryString, primitiveDecoder } from 'hooks/useQueryString'
import { ORDER } from 'routes'

const SESSIONSTORAGE_KEY = 'hub-search'

const PREFIX_FILTER_QUERY_STRING = 'f_'
const KEY_SORT_QUERY_STRING = 'sort'
const QS_ARRAY_OPTIONS = {
  stringify: {},
  parse: {
    decoder: primitiveDecoder,
  },
}

export function UNSAFE_getSearchContext(sessionKey) {
  const searchSession = JSON.parse(sessionStorage.getItem(SESSIONSTORAGE_KEY) || '[]')
  return searchSession.find(s => s.key === sessionKey)
}

export function persistSearchContext(state, sessionKey) {
  const current = JSON.parse(sessionStorage.getItem(SESSIONSTORAGE_KEY) || '[]')
  const others = current.filter(s => s.key !== sessionKey)
  others.push({ key: sessionKey, ...state })
  return sessionStorage.setItem(SESSIONSTORAGE_KEY, JSON.stringify(others))
}

export function useInitSearchSession({ ignoreSession, forceSessionKey }) {
  const { pathname } = useLocation()
  const sessionKey = ignoreSession ? undefined : (forceSessionKey ?? pathname)
  const session = ignoreSession ? undefined : UNSAFE_getSearchContext(sessionKey)

  return { sessionKey, session: session ?? {} }
}

const JSON_VALUE_PREFIXES = ['{', '[']

function decodeSortValue(rawValue) {
  // Nested sort values (e.g. endpointReadiness: { organizationId, direction }) are persisted as JSON;
  // flat direction strings (DESC/ASC) round-trip unchanged. Arrays are covered too so this stays the exact
  // inverse of encodeSortValue, which JSON-encodes any object (and `typeof [] === 'object'`).
  if (typeof rawValue === 'string' && JSON_VALUE_PREFIXES.some(prefix => rawValue.startsWith(prefix))) {
    try {
      return JSON.parse(rawValue)
    } catch {
      return rawValue
    }
  }
  return rawValue
}

export function getSortStateFromQueryString({ search, sortKey = KEY_SORT_QUERY_STRING }) {
  const sortParam = search[sortKey]
  if (!sortParam) return
  const separatorIndex = sortParam.indexOf(':')
  if (separatorIndex === -1) return
  const sortField = sortParam.slice(0, separatorIndex)
  const sortOrder = decodeSortValue(sortParam.slice(separatorIndex + 1))
  return { [sortField]: sortOrder }
}

function getFilterStateFromQueryString({ prefix, search }) {
  const searchKeys = Object.keys(search).filter(key => key.startsWith(prefix))

  const searchKeysresult = searchKeys.reduce((acc, key) => {
    const name = key.replace(prefix, '')
    acc[name] = search[key]
    return acc
  }, {})

  return searchKeysresult
}

export function cleanPersistedFilters({ filters, ignorePersistedFilters }) {
  if (!filters || !ignorePersistedFilters) return filters

  const copy = { ...filters }
  for (const key of ignorePersistedFilters) {
    delete copy[key]
  }

  return copy
}

export function formatValue(value) {
  // In some cases such as the isGeneric filter on product list we want to set a filter as null
  if (value === null) return 'null'
  if (typeof value === 'string' && value === '') return undefined
  if (Array.isArray(value) && value.length === 0) return undefined
  return value
}

function getFiltersQueryStringFromState({ prefix, state, lastSaved }) {
  if (!state) return

  const stateKeys = Object.keys(state)

  const formattedQsParams = stateKeys.reduce((acc, key) => {
    const name = `${prefix}${key}`
    const value = formatValue(state[key])

    acc[name] = value
    return acc
  }, {})

  if (lastSaved) {
    const formattedStateKeys = Object.keys(formattedQsParams)
    const previousKeys = Object.keys(lastSaved).filter(key => !formattedStateKeys.includes(key))
    for (const key of previousKeys) {
      formattedQsParams[key] = undefined
    }
  }

  return formattedQsParams
}

function encodeSortValue(value) {
  // Nested sort values (e.g. endpointReadiness: { organizationId, direction }) are persisted as JSON
  // so they survive the round-trip; flat direction strings keep their plain form.
  if (value !== null && typeof value === 'object') {
    return JSON.stringify(value)
  }
  return formatValue(value)
}

export function getSortQueryStringFromState({ state, sortKey = KEY_SORT_QUERY_STRING }) {
  if (!state) return

  const stateKeys = Object.keys(state)

  const formattedQsParams = stateKeys.reduce((acc, key) => {
    const name = sortKey
    const value = `${key}:${encodeSortValue(state[key])}`

    acc[name] = value
    return acc
  }, {})

  return formattedQsParams
}

export function useInitQueryString({ ignoreQueryStringPersistence }) {
  const qs = useQueryString()
  if (ignoreQueryStringPersistence) return {}
  const search = qs.get(QS_ARRAY_OPTIONS.parse)

  const filters = getFilterStateFromQueryString({
    prefix: PREFIX_FILTER_QUERY_STRING,
    search,
  })

  const sort = getSortStateFromQueryString({
    search,
  })

  return { filters, sort }
}

function formatSort(sort) {
  if (!sort) return
  return Array.isArray(sort) ? sort[0] : sort
}

export function useSearchPersistence({ state, sessionKey, ignoreQueryStringPersistence }) {
  const silentPersistQueryString = useSilentPersistQueryString()
  const lastFiltersSaved = useRef({})

  useEffect(() => {
    if (!ignoreQueryStringPersistence) {
      const filtersQs = getFiltersQueryStringFromState({
        prefix: PREFIX_FILTER_QUERY_STRING,
        state: state.filters,
        lastSaved: lastFiltersSaved.current,
      })

      const sortQs = getSortQueryStringFromState({ state: formatSort(state.sort) })

      silentPersistQueryString({ ...filtersQs, ...sortQs }, QS_ARRAY_OPTIONS.stringify)
      lastFiltersSaved.current = filtersQs
    }
  }, [ignoreQueryStringPersistence, silentPersistQueryString, state])

  useEffect(() => {
    if (sessionKey) {
      persistSearchContext(state, sessionKey)
    }
  }, [state, sessionKey])
}

export function formatFilterQueryString({ key, value }) {
  return { key: `${PREFIX_FILTER_QUERY_STRING}${key}`, value }
}

export function formatSortQueryString({ key, value }) {
  return { key: KEY_SORT_QUERY_STRING, value: `${key}:${value}` }
}

export function clearOrdersPersistedFilters() {
  const current = JSON.parse(sessionStorage.getItem(SESSIONSTORAGE_KEY) || '[]')
  const filtered = current.filter(s => s.key !== ORDER())
  sessionStorage.setItem(SESSIONSTORAGE_KEY, JSON.stringify(filtered))
}
